import { useEffect, useRef, useCallback, useState } from 'react';

const useWebSocket = (url, options = {}) => {
  const {
    onOpen,
    onMessage,
    onClose,
    onError,
    reconnect = true,
    reconnectAttempts = 5,
    reconnectInterval = 3000
  } = options;

  const websocketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState('Connecting');
  const [reconnectCount, setReconnectCount] = useState(0);

  const connect = useCallback(() => {
    //console.log("📡 Connecting to WebSocket at:", url);

    if (!url) return;

    try {
      const websocket = new WebSocket(url);
      websocketRef.current = websocket;
      //console.log("📡 Connecting to WebSocket at:", url);

      websocket.onopen = (event) => {
        //console.log('WebSocket connected:', url);
        setConnectionStatus('Connected');
        setReconnectCount(0);
        if (onOpen) onOpen(event);
      };

      websocket.onmessage = (event) => {
        //console.log("🔥 WebSocket Raw Event:", event);  // Add this line
        if (onMessage) onMessage(event);
      };
      

      websocket.onclose = (event) => {
        //console.log('WebSocket disconnected:', event.code, event.reason);
        setConnectionStatus('Disconnected');
        if (onClose) onClose(event);

        // Attempt to reconnect if enabled and not a normal closure
        if (reconnect && event.code !== 1000 && reconnectCount < reconnectAttempts) {
          setConnectionStatus('Reconnecting');
          reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectCount(prev => prev + 1);
            connect();
          }, reconnectInterval);
        }
      };

      websocket.onerror = (error) => {
        //console.error('WebSocket error:', error);
        setConnectionStatus('Error');
        if (onError) onError(error);
      };

    } catch (error) {
      //console.error('Failed to create WebSocket connection:', error);
      setConnectionStatus('Error');
    }
  }, [url, onOpen, onMessage, onClose, onError, reconnect, reconnectAttempts, reconnectInterval, reconnectCount]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (websocketRef.current) {
      websocketRef.current.close(1000, 'Component unmounting');
    }
  }, []);

  const sendMessage = useCallback((message) => {
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      websocketRef.current.send(typeof message === 'string' ? message : JSON.stringify(message));
      return true;
    }
    //console.warn('WebSocket is not connected. Message not sent:', message);
    return false;
  }, []);

  useEffect(() => {
    connect();
    return disconnect;
  }, [connect, disconnect]);

  return {
    sendMessage,
    disconnect,
    reconnect: connect,
    connectionStatus,
    isConnected: connectionStatus === 'Connected'
  };
};

export default useWebSocket;